import {type Directive, type DirectiveBinding, type VNode} from 'vue';
import {setAlignment} from '@/library/dom';
import {isDeveloper} from '@/utils/portuna';

let _tooltip: HTMLElement;
let _text: HTMLElement;
let _activeElement: Nullable<HTMLElement>;

interface TooltipOptions
{
    text: (() => string) | string,
    align: AlignmentPosition
    css?: object,
    accessible?: boolean
}

interface TooltipVNode extends VNode
{
    mouseListener: VoidFunction;
    tooltipOptions: TooltipOptions;
}

function updateTooltip(ref: HTMLElement, {text, css = {}, align = 'bottom', accessible = false}: TooltipOptions)
{
    if (typeof text === 'function')
    {
        text = text();
    }

    let title = (text || '').trim();
    if (!title.length)
    {
        _tooltip.classList.remove('active');
        return;
    }

    _tooltip.classList.add('active');

    if (accessible)
    {
        _tooltip.classList.add('tooltip-accessible');
    }
    else
    {
        _tooltip.classList.remove('tooltip-accessible');
    }

    Object.assign(_tooltip.style, css);

    // set title to text content
    _text.innerHTML = title;

    _activeElement = ref;

    setAlignment(_tooltip, ref, {
        fixed: accessible,
        forcePosition: false,
        keepOnScreen: accessible,
        arrow: false,
        position: align,
    });
}

function mouseLeaveCallback(event?: Event)
{
    if (
        _tooltip.classList.contains('tooltip-accessible')
        && event instanceof MouseEvent && event?.type === 'mouseleave' && event.relatedTarget
        && (_tooltip.contains(<HTMLElement>event.relatedTarget) || _activeElement?.contains(<HTMLElement>event.relatedTarget))
    )
    {
        _tooltip.removeEventListener('mouseleave', mouseLeaveCallback);
        _tooltip.addEventListener('mouseleave', mouseLeaveCallback);

        return true;
    }

    _activeElement = null;
    _tooltip.classList.remove('active');
    // remove all style properties
    const styleObj   = _tooltip.style;
    let currentIndex = 0;
    while (styleObj.length - currentIndex)
    {
        let property = styleObj.item(currentIndex);
        if (property === 'top' || property === 'left')
        {
            currentIndex++;
        }
        else
        {
            styleObj.removeProperty(property);
        }
    }

    window.removeEventListener('scroll', mouseLeaveCallback);
    _tooltip.removeEventListener('mouseleave', mouseLeaveCallback);

    return true;
}

function getOptions(el: HTMLElement, definition: DirectiveBinding)
{
    let options: TooltipOptions = {
        align: 'bottom',
        text: '',
        accessible: false,
    };

    // get options by object
    if (typeof definition.value === 'object' && definition.value)
    {
        options = Object.assign(options, definition.value);
    }
    // get text from title attribute
    else if (typeof definition.value === 'undefined')
    {
        options.text = el.getAttribute('data-tooltip') as string || el.title || el.ariaLabel || '';
        el.removeAttribute('title');
        el.setAttribute('data-tooltip', options.text);
    }
    // get text value by non object parameter
    else if (definition.value)
    {
        try
        {
            options.text = 'function' !== typeof definition.value ? definition.value.toString() : definition.value;
        }
        catch (e)
        {
            if (isDeveloper())
            {
                throw new Error((e as Error).message);
            }
        }
    }

    return options;
}

function addMouseListener(el: HTMLElement, vNode: TooltipVNode, over: boolean)
{
    vNode.mouseListener = () =>
    {
        window.addEventListener('scroll', mouseLeaveCallback);
        updateTooltip(el, vNode.tooltipOptions);
    };
    el.addEventListener(over ? 'mouseover' : 'mouseenter', vNode.mouseListener);
    el.addEventListener('focusin', vNode.mouseListener);
}

const tooltip: Directive = {
    mounted(el: HTMLElement, binding: DirectiveBinding, vNode: VNode)
    {
        const over                             = binding.modifiers.over || false;
        // get tooltip options and save into vNode
        (vNode as TooltipVNode).tooltipOptions = getOptions(el, binding);
        // create tooltip if no tooltip element exists
        if (!_tooltip)
        {
            _tooltip = document.createElement('div');
            _text    = document.createElement('span');

            _tooltip.classList.add('tooltip');
            _text.classList.add('tooltip-text');

            // append
            _tooltip.appendChild(_text);
            document.body.appendChild(_tooltip);
        }

        addMouseListener(el, vNode as TooltipVNode, over);
        el.addEventListener(over ? 'mouseout' : 'mouseleave', mouseLeaveCallback);
        el.addEventListener('focusout', mouseLeaveCallback);
        el.addEventListener('click', mouseLeaveCallback);
        el.addEventListener('mouseup', event => {
            if (event.button === 2) {
                mouseLeaveCallback();
            }
        });
        vNode.component?.proxy?.emitter.on('closeTooltip', () => mouseLeaveCallback());
    },
    updated(el: HTMLElement, binding: DirectiveBinding, vNode: VNode, oldVNode: VNode)
    {
        const over = binding.modifiers.over || false;
        // remove old mouse over listener
        if (typeof (oldVNode as TooltipVNode).mouseListener === 'function')
        {
            el.removeEventListener('mouseenter', (oldVNode as TooltipVNode).mouseListener);
        }

        // update vNode tooltip options
        (vNode as TooltipVNode).tooltipOptions = getOptions(el, binding);

        // re-add the event listener (filled with new data)
        addMouseListener(el, (vNode as TooltipVNode), over);

        // re-render tooltip if current tooltip is this element
        if (el === _activeElement)
        {
            updateTooltip(el, (vNode as TooltipVNode).tooltipOptions);
        }
    },
    unmounted(el: HTMLElement, binding: DirectiveBinding, vNode: VNode)
    {
        if (el === _activeElement)
        {
            mouseLeaveCallback();
        }
        vNode.component?.proxy?.emitter.off('closeTooltip', () => mouseLeaveCallback());
    },
};

export default tooltip;